import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../../../../components/base/Modal";
import { Badge, Button, confirmDelete, notifyError } from "../../../../components/ui";
import { usePatrolsStore } from "../../patrolsStore";
import { useBasesLocacionesStore } from "../../basesStore";
import { apiErrorMessage } from "../../services/basesApi";
import type { PatrolRoute } from "../../services/patrolsApi";
import SiteMap from "../../components/SiteMap";
import { DetailDrawer, FormField, Kpi, LoadBlock, Photo, ProgressBar, SearchBox, SiteTree, Toolbar, inputCls, pctTone } from "../../components/console";
import { MethodBadge, treeMatch, useRondinesStatus, useRouteTree } from "./shared";

// Rutas de rondín por base: tarjetas con color, puntos y cumplimiento de la
// semana; drawer con el mapa del circuito y los puntos en orden.

type Draft = Pick<PatrolRoute, "name" | "base_id" | "description" | "color" | "estimatedMinutes" | "status" | "zone">;

export default function RondinesRoutes() {
  const { t } = useTranslation("bases");
  const { routes, checkpoints, dashboard, saveRoute, removeRoute } = usePatrolsStore();
  const bases = useBasesLocacionesStore((s) => s.bases);
  const { status, error, retry } = useRondinesStatus();
  const tree = useRouteTree();
  const [sel, setSel] = useState<string | null>(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState<PatrolRoute | null>(null);
  const [editing, setEditing] = useState<{ id: string | null; draft: Draft } | null>(null);
  const [saving, setSaving] = useState(false);

  const compliance = useMemo(() => Object.fromEntries((dashboard?.byRoute ?? []).map((r) => [r.routeId, r])), [dashboard]);
  const list = routes.filter((r) => treeMatch(sel, { base_id: r.base_id, id: r.id }) && (!q || `${r.name} ${r.baseName}`.toLowerCase().includes(q.toLowerCase())));
  const pointsOf = (id: string) => checkpoints.filter((c) => c.route_id === id).sort((a, b) => a.order - b.order);

  const save = async () => {
    if (!editing) return;
    if (!editing.draft.name.trim() || !editing.draft.base_id) return void notifyError(t("rondines.routes.required"));
    setSaving(true);
    try {
      const saved = await saveRoute(editing.id, editing.draft);
      if (open?.id === saved.id) setOpen({ ...open, ...saved });
      setEditing(null);
    } catch (err) {
      notifyError(apiErrorMessage(err, t("common.saveError")));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: PatrolRoute) => {
    if (!(await confirmDelete({ title: t("rondines.routes.deleteTitle", { name: r.name }), text: t("rondines.routes.deleteText"), cancelText: t("common.cancel"), confirmText: t("common.delete") }))) return;
    try {
      await removeRoute(r.id);
      setOpen(null);
    } catch (err) {
      notifyError(apiErrorMessage(err, t("common.saveError")));
    }
  };

  const patch = (p: Partial<Draft>) => setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...p } } : e));
  const newDraft = (): Draft => ({ name: "", base_id: sel?.startsWith("base:") ? sel.slice(5) : bases[0]?.id ?? "", description: "", color: "#14b8a6", estimatedMinutes: 30, status: "active", zone: "" });

  return (
    <div className="flex h-full w-full overflow-hidden">
      <SiteTree title={t("rondines.treeTitle")} allLabel={t("rondines.allRoutes")} nodes={tree} selected={sel} onSelect={setSel} />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Toolbar>
          <div className="mr-2 flex items-center gap-2">
            <i className="ri-route-line text-lg text-primary-500" />
            <h1 className="text-sm font-semibold text-content">{t("rondines.routes.title")}</h1>
          </div>
          <SearchBox value={q} onChange={setQ} placeholder={t("rondines.routes.search")} />
          <div className="ml-auto">
            <Button size="sm" icon={<i className="ri-add-line" />} disabled={!bases.length} onClick={() => setEditing({ id: null, draft: newDraft() })}>
              {t("rondines.routes.new")}
            </Button>
          </div>
        </Toolbar>
        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
            <Kpi icon="ri-route-line" label={t("rondines.kpi.routes")} value={routes.length} />
            <Kpi icon="ri-map-pin-line" label={t("rondines.kpi.checkpointsShort")} value={checkpoints.length} tone="info" />
            <Kpi icon="ri-calendar-2-line" label={t("rondines.kpi.week")} value={dashboard?.week.compliancePct == null ? "—" : `${dashboard.week.compliancePct}%`} tone={pctTone(dashboard?.week.compliancePct)} />
            <Kpi icon="ri-time-line" label={t("rondines.routes.avgMinutes")} value={routes.length ? Math.round(routes.reduce((a, r) => a + (r.estimatedMinutes ?? 0), 0) / routes.length) : 0} tone="primary" />
          </div>
          <LoadBlock status={status} error={error} onRetry={retry} empty={routes.length === 0} emptyIcon="ri-route-line" emptyTitle={t("rondines.routes.emptyTitle")} emptyText={t("rondines.routes.emptyText")}>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
              {list.map((r) => {
                const pts = pointsOf(r.id);
                const c = compliance[r.id];
                return (
                  <button key={r.id} type="button" onClick={() => setOpen(r)} className="flex overflow-hidden rounded-xl border border-line bg-card text-left transition-shadow hover:shadow-lg">
                    <span className="w-1.5 shrink-0" style={{ background: r.color || "#14b8a6" }} />
                    <div className="flex min-w-0 flex-1 flex-col gap-2 p-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-content">{r.name}</div>
                          <div className="truncate text-xs text-content-secondary">{r.baseName}</div>
                        </div>
                        <Badge size="sm" tone={r.status === "active" ? "success" : "neutral"} dot>
                          {t(`rondines.routeStatus.${r.status}`)}
                        </Badge>
                      </div>
                      <div className="flex -space-x-2">
                        {pts.slice(0, 6).map((p) => (
                          <Photo key={p.id} src={p.photo} alt={p.name} thumb className="h-10 w-14 rounded-md ring-2 ring-card" icon="ri-map-pin-line" />
                        ))}
                      </div>
                      <div>
                        <div className="mb-1 flex justify-between text-[11px] text-content-muted">
                          <span>{t("rondines.routes.weekCompliance")}</span>
                          <span className="tabular-nums">{c?.compliancePct == null ? "—" : `${c.compliancePct}%`}</span>
                        </div>
                        <ProgressBar pct={c?.compliancePct ?? 0} tone={pctTone(c?.compliancePct)} />
                      </div>
                      <div className="flex justify-between text-xs text-content-secondary">
                        <span><i className="ri-map-pin-line" /> {t("rondines.routes.points", { n: pts.length })}</span>
                        <span><i className="ri-time-line" /> {r.estimatedMinutes ?? "—"} min</span>
                        <span><i className="ri-repeat-line" /> {t("rondines.routes.rounds", { n: c?.rounds ?? 0 })}</span>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </LoadBlock>
        </div>
      </div>

      <DetailDrawer
        open={Boolean(open)}
        onClose={() => setOpen(null)}
        title={open?.name}
        subtitle={open?.baseName}
        actions={
          open ? (
            <>
              <Button size="sm" variant="soft" icon={<i className="ri-edit-line" />} onClick={() => setEditing({ id: open.id, draft: { name: open.name, base_id: open.base_id, description: open.description ?? "", color: open.color ?? "#14b8a6", estimatedMinutes: open.estimatedMinutes ?? 30, status: open.status, zone: open.zone ?? "" } })}>
                {t("common.edit")}
              </Button>
              <Button size="sm" variant="dangerSoft" iconOnly icon={<i className="ri-delete-bin-line" />} aria-label={t("common.delete")} onClick={() => void remove(open)} />
            </>
          ) : null
        }
      >
        {open ? (
          <div className="space-y-4">
            {open.description ? <p className="text-sm text-content-secondary">{open.description}</p> : null}
            {pointsOf(open.id).some((p) => typeof p.latitude === "number") ? (
              <div className="overflow-hidden rounded-xl border border-line">
                <SiteMap
                  path
                  pathColor={open.color || undefined}
                  points={pointsOf(open.id)
                    .filter((p) => typeof p.latitude === "number" && typeof p.longitude === "number")
                    .map((p) => ({ id: p.id, lat: p.latitude as number, lng: p.longitude as number, label: `${p.order}. ${p.name}`, order: p.order, color: open.color }))}
                  className="h-56 w-full"
                />
              </div>
            ) : null}
            <ol className="space-y-2">
              {pointsOf(open.id).map((p) => (
                <li key={p.id} className="flex items-center gap-3 rounded-lg border border-line bg-card p-2">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white" style={{ background: open.color || "#14b8a6" }}>
                    {p.order}
                  </span>
                  <Photo src={p.photo} alt={p.name} thumb className="h-12 w-16 shrink-0 rounded-md" icon="ri-map-pin-line" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-content">{p.name}</div>
                    <div className="text-[11px] text-content-muted">
                      {p.code} · {p.zone}
                    </div>
                  </div>
                  <MethodBadge method={p.method} />
                </li>
              ))}
            </ol>
            {pointsOf(open.id).length === 0 ? <p className="text-xs text-content-muted">{t("rondines.checkpoints.emptyTitle")}</p> : null}
          </div>
        ) : null}
      </DetailDrawer>

      {editing ? (
        <Modal
          title={editing.id ? t("rondines.routes.edit") : t("rondines.routes.new")}
          onClose={() => setEditing(null)}
          content={
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <FormField label={t("fields.name")} className="sm:col-span-2">
                <input className={inputCls} value={editing.draft.name} onChange={(e) => patch({ name: e.target.value })} />
              </FormField>
              <FormField label={t("fields.base")}>
                <select className={inputCls} value={editing.draft.base_id} onChange={(e) => patch({ base_id: e.target.value })}>
                  {bases.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("fields.status")}>
                <select className={inputCls} value={editing.draft.status} onChange={(e) => patch({ status: e.target.value as Draft["status"] })}>
                  {(["active", "inactive"] as const).map((s) => (
                    <option key={s} value={s}>
                      {t(`rondines.routeStatus.${s}`)}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t("rondines.routes.estimated")}>
                <input className={inputCls} type="number" min={5} value={editing.draft.estimatedMinutes ?? 30} onChange={(e) => patch({ estimatedMinutes: Number(e.target.value || 30) })} />
              </FormField>
              <FormField label={t("rondines.routes.color")}>
                <input className={`${inputCls} h-10 p-1`} type="color" value={editing.draft.color ?? "#14b8a6"} onChange={(e) => patch({ color: e.target.value })} />
              </FormField>
              <FormField label={t("fields.description")} className="sm:col-span-2">
                <textarea className={inputCls} rows={2} value={editing.draft.description ?? ""} onChange={(e) => patch({ description: e.target.value })} />
              </FormField>
              <div className="flex justify-end gap-2 border-t border-line pt-4 sm:col-span-2">
                <Button variant="secondary" onClick={() => setEditing(null)}>
                  {t("common.cancel")}
                </Button>
                <Button loading={saving} onClick={() => void save()} icon={<i className="ri-save-line" />}>
                  {t("common.save")}
                </Button>
              </div>
            </div>
          }
        />
      ) : null}
    </div>
  );
}
